/* TerritoryImport — "pick the towns, then import" workflow for rural
 * territories.
 *
 * Top bar: a switcher between every territory the user can see (the ready-made
 * "Central Alberta" catalog territory plus any the team has drawn), and a
 * "Draw a new territory" button (for admins + members with territory access).
 *
 * VIEW mode (a territory selected): a boundary map (box or drawn polygon) +
 * a pin per town on the left, a grouped town checklist on the right. Unchecking
 * a town excludes it from the batch Realtor.ca import, which runs the agent
 * search once per checked town and files the matches into the territory's
 * Location.
 *
 * DRAW mode: draw/reshape an editable polygon → auto-detect the towns inside
 * it (size-tier filtered) → name it + pick the Location → save, which creates
 * the territory and drops you into VIEW mode ready to import.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  MapPinned, Play, Loader2, Info, CheckCircle2, AlertTriangle, Users, Building2,
  Plus, Pencil, Trash2, Search, X, CalendarDays, ArrowRightLeft,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import ConfirmDialog from "@/components/ConfirmDialog";
import CreditEstimate from "@/components/billing/CreditEstimate";
import { useAuth } from "@/contexts/AuthContext";
import TerritoryMap from "@/components/territory/TerritoryMap";

const POLL_MS = 3000;
const TYPE_LABEL = { city: "City", town: "Town", village: "Village", community: "Community" };
const TIER_OPTS = [
  { key: "city", label: "Cities" },
  { key: "town", label: "Towns" },
  { key: "village", label: "Villages" },
  { key: "community", label: "Communities & hamlets" },
];

// A new territory starts as a rectangle pre-seeded with extra vertices along
// each edge so the user can grab a few points and reshape it into any polygon.
const DEFAULT_BOX = { north: 53.1, south: 51.6, west: -107.9, east: -105.3 };

function seedRing(box, perEdge = 6) {
  const { north, south, east, west } = box;
  const lerp = (a, b, t) => a + (b - a) * t;
  const steps = perEdge + 1;
  const pts = [];
  for (let i = 0; i < steps; i++) pts.push({ lat: north, lng: lerp(west, east, i / steps) }); // top W→E
  for (let i = 0; i < steps; i++) pts.push({ lat: lerp(north, south, i / steps), lng: east }); // right N→S
  for (let i = 0; i < steps; i++) pts.push({ lat: south, lng: lerp(east, west, i / steps) }); // bottom E→W
  for (let i = 0; i < steps; i++) pts.push({ lat: lerp(south, north, i / steps), lng: west }); // left S→N
  return pts;
}

// ------------------------------------------------------------------ container
export default function TerritoryImport() {
  const { user, refreshMe } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const urlKey = (params.get("key") || "").trim();

  const [territories, setTerritories] = useState([]);
  const [canEdit, setCanEdit] = useState(false);
  const [locations, setLocations] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [mode, setMode] = useState("view"); // view | draw
  const [listLoading, setListLoading] = useState(true);

  const isAdmin = user?.role === "admin";

  const loadList = useCallback(async () => {
    const [tRes, lRes] = await Promise.all([
      api.get("/territories"),
      api.get("/locations", { params: { with_counts: true } }),
    ]);
    const terrs = tRes.data.territories || [];
    setTerritories(terrs);
    setCanEdit(!!tRes.data.can_edit);
    setLocations(lRes.data.locations || []);
    return terrs;
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      setListLoading(true);
      try {
        const terrs = await loadList();
        if (!alive) return;
        let initial = "";
        if (urlKey) initial = terrs.find((t) => t.territory_key === urlKey)?.id || "";
        if (!initial && terrs.length) initial = terrs[0].id;
        setSelectedId(initial);
      } catch (e) {
        if (alive) toast.error("Failed to load territories", { description: formatApiError(e) });
      } finally {
        if (alive) setListLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [loadList, urlKey]);

  const onDrawSaved = async (newTerr) => {
    await loadList();
    setSelectedId(newTerr.id);
    setMode("view");
    toast.success("Territory saved — pick your towns and import");
  };

  const onDeleted = async () => {
    const terrs = await loadList();
    setSelectedId(terrs[0]?.id || "");
    setMode("view");
  };

  return (
    <div className="space-y-5" data-testid="territory-import-page">
      <div>
        <p className="text-xs uppercase tracking-wider text-slate-500 font-medium flex items-center gap-1.5">
          <MapPinned className="h-3.5 w-3.5" /> Rural territory import
        </p>
        <h1 className="text-3xl font-bold tracking-tight text-slate-900 font-display">
          Territory import
        </h1>
        <p className="text-sm text-slate-600 mt-1 max-w-2xl">
          Pick (or draw) a rural region, choose the towns inside it, and batch-import
          their Realtor.ca agents in one run.
        </p>
      </div>

      {/* Switcher */}
      <div className="rounded-lg border border-slate-200 bg-white p-3 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="territory-switcher">
        <Label className="text-xs uppercase tracking-wide text-slate-500 shrink-0">Territory</Label>
        <div className="flex-1 min-w-0">
          <Select
            value={selectedId}
            onValueChange={(v) => { setSelectedId(v); setMode("view"); }}
            disabled={mode === "draw" || territories.length === 0}
          >
            <SelectTrigger className="min-h-[44px]" data-testid="territory-select">
              <SelectValue placeholder={territories.length ? "Select a territory" : "No territories yet"} />
            </SelectTrigger>
            <SelectContent>
              {territories.map((t) => (
                <SelectItem key={t.id} value={t.id} data-testid={`territory-option-${t.id}`}>
                  {t.name} · {t.town_count} town{t.town_count === 1 ? "" : "s"}
                  {t.is_shared ? "  (shared)" : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {canEdit ? (
          mode === "draw" ? (
            <Button
              variant="outline"
              className="min-h-[44px] shrink-0"
              onClick={() => setMode("view")}
              data-testid="territory-cancel-draw"
            >
              <X className="h-4 w-4 mr-2" /> Cancel drawing
            </Button>
          ) : (
            <Button
              className="text-white min-h-[44px] shrink-0"
              style={{ background: "hsl(var(--brand))" }}
              onClick={() => setMode("draw")}
              data-testid="territory-draw-new"
            >
              <Plus className="h-4 w-4 mr-2" /> Draw a new territory
            </Button>
          )
        ) : null}
      </div>

      {listLoading ? (
        <div className="flex items-center justify-center p-16 text-sm text-slate-500" data-testid="territory-loading">
          <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading territories…
        </div>
      ) : mode === "draw" ? (
        <TerritoryDrawPanel
          locations={locations}
          onCancel={() => setMode("view")}
          onSaved={onDrawSaved}
        />
      ) : selectedId ? (
        <TerritoryView
          key={selectedId}
          territoryId={selectedId}
          isAdmin={isAdmin}
          userId={user?.id}
          refreshMe={refreshMe}
          navigate={navigate}
          onDeleted={onDeleted}
        />
      ) : (
        <div className="rounded-lg border border-dashed border-slate-200 bg-white p-10 text-center" data-testid="territory-empty-state">
          <MapPinned className="h-6 w-6 mx-auto text-slate-300 mb-2" />
          <p className="text-sm text-slate-600">No territories yet.</p>
          {canEdit ? (
            <Button
              className="mt-3 text-white min-h-[44px]"
              style={{ background: "hsl(var(--brand))" }}
              onClick={() => setMode("draw")}
              data-testid="territory-empty-draw"
            >
              <Plus className="h-4 w-4 mr-2" /> Draw your first territory
            </Button>
          ) : (
            <p className="text-xs text-slate-500 mt-1">Ask an admin to grant you territory access.</p>
          )}
        </div>
      )}
    </div>
  );
}

// ------------------------------------------------------------------ draw panel
function TerritoryDrawPanel({ locations, onCancel, onSaved }) {
  const [seed] = useState(() => seedRing(DEFAULT_BOX, 6)); // stable seed ref
  const pathRef = useRef(seed);
  const [tiers, setTiers] = useState(() => new Set(["city", "town", "village", "community"]));
  const [detecting, setDetecting] = useState(false);
  const [detected, setDetected] = useState(null); // { towns, counts, groups }
  const [name, setName] = useState("");
  const [locId, setLocId] = useState(() => locations[0]?.id || "");
  const [saving, setSaving] = useState(false);

  const onPathChange = useCallback((arr) => { pathRef.current = arr; }, []);

  const toggleTier = (t) => {
    setTiers((prev) => {
      const n = new Set(prev);
      if (n.has(t)) n.delete(t); else n.add(t);
      return n;
    });
  };

  const detect = async () => {
    const path = pathRef.current;
    if (!path || path.length < 3) { toast.error("Draw a boundary first"); return; }
    if (tiers.size === 0) { toast.error("Pick at least one size tier"); return; }
    setDetecting(true);
    try {
      const polygon = path.map((p) => [p.lat, p.lng]);
      const { data } = await api.post("/territories/detect-towns", {
        boundary: { type: "polygon", polygon },
        tiers: Array.from(tiers),
      });
      setDetected(data);
      const total = (data.groups || []).reduce((n, g) => n + g.towns.length, 0);
      if (total === 0) {
        toast.message("No towns found inside that boundary", {
          description: "Try enlarging the shape or enabling more size tiers.",
        });
      } else {
        toast.success(`Found ${total} town${total === 1 ? "" : "s"}`);
      }
    } catch (e) {
      toast.error("Town detection failed", { description: formatApiError(e) });
    } finally {
      setDetecting(false);
    }
  };

  const save = async () => {
    if (!name.trim()) { toast.error("Name your territory"); return; }
    const total = (detected?.groups || []).reduce((n, g) => n + g.towns.length, 0);
    if (!detected || total === 0) { toast.error("Detect towns first"); return; }
    setSaving(true);
    try {
      const polygon = pathRef.current.map((p) => [p.lat, p.lng]);
      const body = {
        name: name.trim(),
        boundary: { type: "polygon", polygon },
        tiers: Array.from(tiers),
        groups: detected.groups,
      };
      if (locId) body.location_id = locId;
      const { data } = await api.post("/territories", body);
      onSaved(data);
    } catch (e) {
      toast.error("Couldn't save territory", { description: formatApiError(e) });
    } finally {
      setSaving(false);
    }
  };

  const detectedTowns = useMemo(
    () => (detected?.groups || []).flatMap((g) => g.towns),
    [detected],
  );
  const detectedTotal = detectedTowns.length;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-4" data-testid="territory-draw-panel">
      <div className="lg:col-span-3">
        <TerritoryMap drawMode drawPolygon={seed} onPathChange={onPathChange} towns={detectedTowns} />
      </div>

      <div className="lg:col-span-2 rounded-lg border border-slate-200 bg-white flex flex-col">
        <div className="px-4 py-3 border-b border-slate-200">
          <div className="text-sm font-semibold text-slate-900">Draw a new territory</div>
          <div className="text-[11px] text-slate-500 mt-0.5">
            Reshape the boundary on the map, then detect the towns inside it.
          </div>
        </div>

        <div className="p-4 space-y-4">
          {/* Size tiers */}
          <div>
            <Label className="text-xs uppercase tracking-wide text-slate-500">Include size tiers</Label>
            <div className="mt-1.5 grid grid-cols-2 gap-1.5">
              {TIER_OPTS.map((t) => (
                <label
                  key={t.key}
                  className="flex items-center gap-2 rounded-md border border-slate-200 px-2.5 py-2 text-sm cursor-pointer hover:bg-slate-50"
                >
                  <Checkbox
                    checked={tiers.has(t.key)}
                    onCheckedChange={() => toggleTier(t.key)}
                    data-testid={`territory-draw-tier-${t.key}`}
                  />
                  <span className="text-slate-700 truncate">{t.label}</span>
                </label>
              ))}
            </div>
          </div>

          <Button
            onClick={detect}
            disabled={detecting}
            variant="outline"
            className="w-full min-h-[44px]"
            data-testid="territory-detect-towns"
          >
            {detecting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Search className="h-4 w-4 mr-2" />}
            {detecting ? "Detecting towns…" : "Detect towns inside boundary"}
          </Button>

          {/* Detection result */}
          {detected ? (
            <div className="rounded-md border border-slate-200 overflow-hidden" data-testid="territory-detect-result">
              <div className="px-3 py-2 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
                <span className="text-xs font-semibold text-slate-700">
                  {detectedTotal} town{detectedTotal === 1 ? "" : "s"} found
                </span>
                <span className="text-[11px] text-slate-500 tabular-nums">
                  {(detected.counts?.city || 0)}c · {(detected.counts?.town || 0)}t · {(detected.counts?.village || 0)}v · {(detected.counts?.community || 0)}h
                </span>
              </div>
              {detectedTotal > 0 ? (
                <div className="max-h-48 overflow-y-auto divide-y divide-slate-50">
                  {detected.groups.map((g) => (
                    <div key={g.key} className="px-3 py-1.5">
                      <div className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold">
                        {g.label} · {g.towns.length}
                      </div>
                      <div className="text-xs text-slate-600 mt-0.5 leading-relaxed">
                        {g.towns.map((t) => t.name).join(", ")}
                      </div>
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
          ) : null}

          {/* Name + location + save */}
          <div className="space-y-3 pt-2 border-t border-slate-100">
            <div>
              <Label className="text-xs uppercase tracking-wide text-slate-500">Territory name</Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Southeast Saskatchewan"
                className="mt-1 min-h-[44px]"
                data-testid="territory-draw-name"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide text-slate-500">Import into location</Label>
              <Select value={locId} onValueChange={setLocId} disabled={locations.length === 0}>
                <SelectTrigger className="mt-1 min-h-[44px]" data-testid="territory-draw-location">
                  <SelectValue placeholder={locations.length ? "Select a location" : "No locations available"} />
                </SelectTrigger>
                <SelectContent>
                  {locations.map((l) => (
                    <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-slate-400 mt-1">Imported realtors are filed into this workspace.</p>
            </div>
            <div className="flex gap-2">
              <Button variant="ghost" className="min-h-[44px]" onClick={onCancel} data-testid="territory-draw-cancel">
                Cancel
              </Button>
              <Button
                onClick={save}
                disabled={saving || !name.trim() || detectedTotal === 0}
                className="flex-1 text-white min-h-[44px]"
                style={{ background: "hsl(var(--brand))" }}
                data-testid="territory-draw-save"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
                Save territory
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ town row
function formatScanDate(iso) {
  const d = new Date(iso);
  const now = new Date();
  if (d.toDateString() === now.toDateString()) return "Today";
  const opts = { month: "short", day: "numeric" };
  if (d.getFullYear() !== now.getFullYear()) opts.year = "2-digit";
  return d.toLocaleDateString(undefined, opts);
}

function ScanStamp({ town, scan }) {
  if (!scan?.last_scanned_at) {
    return (
      <span
        className="w-[52px] text-right shrink-0"
        title="Never searched — new town"
        data-testid={`territory-town-new-${town}`}
      >
        <span className="inline-block rounded bg-emerald-50 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-emerald-700">New</span>
      </span>
    );
  }
  const when = new Date(scan.last_scanned_at).toLocaleString();
  return (
    <span
      className="w-[52px] text-right text-[10px] tabular-nums text-slate-400 shrink-0 truncate"
      title={`Last searched ${when} · ${scan.found ?? 0} found${scan.scan_count > 1 ? ` · ${scan.scan_count} searches` : ""}`}
      data-testid={`territory-town-scanned-${town}`}
    >
      {formatScanDate(scan.last_scanned_at)}
    </span>
  );
}

function TownRow({ town: t, on, realtorCount, scan, onToggle, otherGroups, onMove }) {
  return (
    <div
      className="group flex items-center gap-2.5 px-3 py-1.5 hover:bg-slate-50"
      data-testid={`territory-town-${t.name}`}
    >
      <Checkbox checked={on} onCheckedChange={onToggle} data-testid={`territory-town-check-${t.name}`} />
      <button
        type="button"
        onClick={onToggle}
        className={"text-sm flex-1 min-w-0 truncate text-left " + (on ? "text-slate-900" : "text-slate-400")}
      >
        {t.name}
      </button>
      <span
        className={"text-[11px] tabular-nums shrink-0 " + (realtorCount ? "text-slate-700 font-medium" : "text-slate-300")}
        data-testid={`territory-town-realtors-${t.name}`}
        title={realtorCount ? `${realtorCount} realtor${realtorCount === 1 ? "" : "s"} tagged ${t.name}` : "No realtors imported yet"}
      >
        {realtorCount ? `${realtorCount} realtor${realtorCount === 1 ? "" : "s"}` : "—"}
      </span>
      <ScanStamp town={t.name} scan={scan} />
      {t.is_city ? (
        <span className="w-[76px] text-right text-[9px] uppercase tracking-wide font-semibold text-[#C8102E] shrink-0">
          <span className="bg-red-50 rounded px-1.5 py-0.5">City</span>
        </span>
      ) : (
        <span className="w-[76px] text-right text-[9px] uppercase tracking-wide text-slate-400 shrink-0">{TYPE_LABEL[t.type] || t.type}</span>
      )}
      {otherGroups.length ? (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="h-6 w-6 shrink-0 inline-flex items-center justify-center rounded text-slate-400 hover:text-slate-900 hover:bg-slate-100"
              title="Move to another division"
              data-testid={`territory-town-move-${t.name}`}
            >
              <ArrowRightLeft className="h-3.5 w-3.5" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-64">
            <DropdownMenuLabel className="text-[11px] uppercase tracking-wide text-slate-500">Move {t.name} to…</DropdownMenuLabel>
            {otherGroups.map((g) => (
              <DropdownMenuItem
                key={g.key}
                onClick={() => onMove(g.key)}
                className="text-sm"
                data-testid={`territory-town-move-${t.name}-to-${g.key}`}
              >
                {g.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      ) : null}
    </div>
  );
}

// ------------------------------------------------------------------ view + import
function TerritoryView({ territoryId, isAdmin, userId, refreshMe, navigate, onDeleted }) {
  const [territory, setTerritory] = useState(null);
  const [location, setLocation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadErr, setLoadErr] = useState("");
  const [checked, setChecked] = useState(() => new Set());
  const [job, setJob] = useState(null);
  const [running, setRunning] = useState(false);
  const [creditsOk, setCreditsOk] = useState(true);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editSeed, setEditSeed] = useState(null);
  const [savingBoundary, setSavingBoundary] = useState(false);
  const editPathRef = useRef([]);
  const onEditPath = useCallback((arr) => { editPathRef.current = arr; }, []);

  const allTowns = useMemo(
    () => (territory?.groups || []).flatMap((g) => g.towns),
    [territory],
  );
  const newTownNames = useMemo(
    () => allTowns.filter((t) => !territory?.last_scanned?.[t.name]?.last_scanned_at).map((t) => t.name),
    [allTowns, territory],
  );

  const refreshTerritory = useCallback(async () => {
    try {
      const { data } = await api.get(`/territories/${territoryId}`);
      setTerritory(data);
    } catch { /* ignore */ }
  }, [territoryId]);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      setLoadErr("");
      try {
        const [tRes, lRes] = await Promise.all([
          api.get(`/territories/${territoryId}`),
          api.get("/locations", { params: { with_counts: true } }),
        ]);
        if (!alive) return;
        const terr = tRes.data;
        setTerritory(terr);
        const names = (terr.groups || []).flatMap((g) => g.towns.map((t) => t.name));
        setChecked(new Set(names));
        const loc = (lRes.data.locations || []).find((l) => l.id === terr.location_id) || null;
        setLocation(loc);
      } catch (e) {
        if (alive) setLoadErr(formatApiError(e));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [territoryId]);

  // Resume an in-flight territory import.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/scrape-jobs", { params: { type: "realtor_ca_import", limit: 1 } });
        const j = data?.[0];
        if (j && j.summary?.import_mode === "territory_batch" && (j.status === "running" || j.status === "queued")) {
          setJob(j);
        }
      } catch { /* ignore */ }
    })();
  }, []);

  useEffect(() => {
    if (!job || (job.status !== "running" && job.status !== "queued")) {
      setRunning(false);
      return undefined;
    }
    setRunning(true);
    const t = setInterval(async () => {
      try {
        const { data } = await api.get(`/scrape-jobs/${job.id}`);
        setJob(data);
        if (data.status !== "running" && data.status !== "queued") {
          setRunning(false);
          refreshTerritory();
          if (location) {
            try {
              const { data: lRes } = await api.get("/locations", { params: { with_counts: true } });
              const loc = (lRes.locations || []).find((l) => l.id === location.id);
              if (loc) setLocation(loc);
            } catch { /* ignore */ }
          }
        }
      } catch { /* ignore */ }
    }, POLL_MS);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job?.id, job?.status]);

  const toggleTown = useCallback((nameVal) => {
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(nameVal)) next.delete(nameVal); else next.add(nameVal);
      return next;
    });
  }, []);

  const setGroup = useCallback((group, on) => {
    setChecked((prev) => {
      const next = new Set(prev);
      for (const t of group.towns) { if (on) next.add(t.name); else next.delete(t.name); }
      return next;
    });
  }, []);

  const setAll = useCallback((on) => {
    if (on) setChecked(new Set(allTowns.map((t) => t.name)));
    else setChecked(new Set());
  }, [allTowns]);

  const selectNewTowns = useCallback(() => {
    setChecked(new Set(newTownNames));
    if (newTownNames.length === 0) toast.info("No new towns — every town here has been searched before");
  }, [newTownNames]);

  const startImport = async () => {
    if (!location) { toast.error("No location linked to this territory"); return; }
    const towns = allTowns.map((t) => t.name).filter((n) => checked.has(n));
    if (towns.length === 0) { toast.error("Select at least one town"); return; }
    setRunning(true);
    setJob(null);
    try {
      const { data } = await api.post("/system/realtor-ca/import-territory", {
        location_id: location.id,
        territory_key: territory.territory_key || "",
        towns,
        province: territory.province,
      });
      const { data: jobDoc } = await api.get(`/scrape-jobs/${data.job_id}`);
      setJob(jobDoc);
      toast.success(`Territory import started · ${towns.length} town(s)`, {
        description: "Watch the progress below — it runs one search per town.",
      });
    } catch (e) {
      setRunning(false);
      toast.error("Failed to start import", { description: formatApiError(e) });
    }
  };

  const viewRealtors = async () => {
    if (!location) return;
    try {
      if (isAdmin) {
        await api.put("/me/active-locations", { location_ids: [location.id] });
        await refreshMe?.();
      }
    } catch { /* ignore */ }
    navigate("/realtors");
  };

  const doDelete = async () => {
    try {
      await api.delete(`/territories/${territoryId}`);
      toast.success("Territory deleted");
      setConfirmDelete(false);
      onDeleted();
    } catch (e) {
      toast.error("Delete failed", { description: formatApiError(e) });
    }
  };

  const moveTown = async (townName, toGroup) => {
    try {
      const { data } = await api.post(`/territories/${territoryId}/move-town`, { town: townName, to_group: toGroup });
      setTerritory(data);
      const label = (data.groups || []).find((g) => g.key === toGroup)?.label || toGroup;
      toast.success(`${townName} moved to ${label.split(" — ")[0]}`);
    } catch (e) {
      toast.error("Couldn't move town", { description: formatApiError(e) });
    }
  };

  const scanOpenHouses = () => navigate(`/open-houses?scan=territory&territory=${territoryId}`);

  const startEdit = () => {
    const b = territory.boundary || {};
    let ring;
    if (b.type === "polygon" && Array.isArray(b.polygon) && b.polygon.length >= 3) {
      ring = b.polygon.map((p) => ({ lat: p[0], lng: p[1] }));
    } else if (territory.box) {
      ring = seedRing(territory.box, 6);
    } else {
      ring = seedRing(DEFAULT_BOX, 6);
    }
    editPathRef.current = ring;
    setEditSeed(ring);
    setEditing(true);
  };

  const saveBoundary = async () => {
    const path = editPathRef.current;
    if (!path || path.length < 3) { toast.error("Reshape the boundary first"); return; }
    setSavingBoundary(true);
    try {
      const polygon = path.map((p) => [p.lat, p.lng]);
      const det = await api.post("/territories/detect-towns", {
        boundary: { type: "polygon", polygon },
        tiers: ["city", "town", "village", "community"],
      });
      const groups = det.data.groups || [];
      const { data } = await api.patch(`/territories/${territoryId}`, {
        boundary: { type: "polygon", polygon },
        groups,
      });
      setTerritory(data);
      const names = (data.groups || []).flatMap((g) => g.towns.map((t) => t.name));
      const fresh = names.filter((n) => !data.last_scanned?.[n]?.last_scanned_at);
      setChecked(new Set(fresh));
      setEditing(false);
      toast.success(`Boundary updated · ${names.length} town${names.length === 1 ? "" : "s"} now inside`, {
        description: fresh.length
          ? `${fresh.length} new town${fresh.length === 1 ? "" : "s"} never searched — selected for import.`
          : "Every town inside has been searched before. Nothing pre-selected.",
      });
    } catch (e) {
      toast.error("Couldn't save boundary", { description: formatApiError(e) });
    } finally {
      setSavingBoundary(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-16 text-sm text-slate-500" data-testid="territory-view-loading">
        <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading territory…
      </div>
    );
  }
  if (loadErr || !territory) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-6 text-sm text-red-800" data-testid="territory-view-error">
        <AlertTriangle className="h-5 w-5 mb-2" />
        Couldn't load the territory. {loadErr}
      </div>
    );
  }

  const checkedCount = checked.size;
  const summary = job?.summary || {};
  const isTerritoryJob = summary.import_mode === "territory_batch";
  const done = job && job.status !== "running" && job.status !== "queued";
  const outlinePolygon = (territory.boundary?.polygon || []).map((p) => ({ lat: p[0], lng: p[1] }));
  const canDelete = isAdmin || (!territory.is_shared && territory.owner_user_id === userId);

  return (
    <div className="space-y-5" data-testid="territory-view">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h2 className="text-xl font-bold tracking-tight text-slate-900 font-display">{territory.name}</h2>
          {territory.description ? (
            <p className="text-sm text-slate-600 mt-0.5 max-w-2xl">{territory.description}</p>
          ) : null}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {location ? (
            <div className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs text-slate-600" data-testid="territory-location-chip">
              <div className="flex items-center gap-1.5 font-medium text-slate-900">
                <Building2 className="h-3.5 w-3.5 text-slate-400" /> {location.name}
              </div>
              <div className="mt-0.5 flex items-center gap-1 text-slate-500">
                <Users className="h-3 w-3" /> {location.realtor_count ?? 0} realtors · {location.currency}
              </div>
            </div>
          ) : null}
          {!editing ? (
            <Button
              variant="outline"
              className="min-h-[44px]"
              onClick={scanOpenHouses}
              data-testid="territory-scan-open-houses"
              title="Scan Realtor.ca open houses inside this territory"
            >
              <CalendarDays className="h-4 w-4 mr-2" /> Scan open houses
            </Button>
          ) : null}
          {territory.editable && !editing ? (
            <Button
              variant="outline"
              className="min-h-[44px]"
              onClick={startEdit}
              data-testid="territory-edit-boundary"
            >
              <Pencil className="h-4 w-4 mr-2" /> Edit boundary
            </Button>
          ) : null}
          {canDelete ? (
            <Button
              variant="outline"
              className="min-h-[44px] border-rose-200 text-rose-700 hover:bg-rose-50"
              onClick={() => setConfirmDelete(true)}
              data-testid="territory-delete"
              title="Delete territory"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          ) : null}
        </div>
      </div>

      {editing ? (
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4" data-testid="territory-edit-panel">
          <div className="lg:col-span-3">
            <TerritoryMap drawMode drawPolygon={editSeed} onPathChange={onEditPath} towns={allTowns} />
          </div>
          <div className="lg:col-span-2 rounded-lg border border-slate-200 bg-white p-4 flex flex-col gap-3">
            <div>
              <div className="text-sm font-semibold text-slate-900">Edit boundary</div>
              <p className="text-[11px] text-slate-500 mt-0.5">
                Drag the dots (4 corners + 6 per side) to reshape this territory. Drag the lighter
                dots on each edge to add new corners. Saving re-detects every town inside the new shape.
              </p>
            </div>
            <div className="flex gap-2 mt-2">
              <Button variant="ghost" className="min-h-[44px]" onClick={() => setEditing(false)} data-testid="territory-edit-cancel">
                Cancel
              </Button>
              <Button
                onClick={saveBoundary}
                disabled={savingBoundary}
                className="flex-1 text-white min-h-[44px]"
                style={{ background: "hsl(var(--brand))" }}
                data-testid="territory-edit-save"
              >
                {savingBoundary ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
                Save boundary
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <>
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        <div className="lg:col-span-3">
          <TerritoryMap
            box={territory.box}
            center={territory.center}
            zoom={territory.default_zoom || 8}
            towns={allTowns}
            checked={checked}
            onToggleTown={toggleTown}
            outlinePolygon={outlinePolygon.length >= 3 ? outlinePolygon : null}
          />
        </div>

        <div className="lg:col-span-2 rounded-lg border border-slate-200 bg-white flex flex-col" data-testid="territory-checklist">
          <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between gap-2">
            <div>
              <div className="text-sm font-semibold text-slate-900">Towns to import</div>
              <div className="text-[11px] text-slate-500" data-testid="territory-checked-count">
                {checkedCount} of {allTowns.length} selected
                {newTownNames.length ? (
                  <span className="text-emerald-700" data-testid="territory-new-count"> · {newTownNames.length} new</span>
                ) : null}
              </div>
            </div>
            <div className="flex gap-1.5">
              <Button variant="outline" size="sm" className="h-7 text-[11px] px-2" onClick={() => setAll(true)} data-testid="territory-select-all">All</Button>
              <Button variant="outline" size="sm" className="h-7 text-[11px] px-2" onClick={() => setAll(false)} data-testid="territory-select-none">None</Button>
              <Button
                variant="outline"
                size="sm"
                className="h-7 text-[11px] px-2 border-emerald-200 text-emerald-700 hover:bg-emerald-50 hover:text-emerald-800"
                onClick={selectNewTowns}
                title="Select only towns that have never been searched"
                data-testid="territory-select-new"
              >
                New towns{newTownNames.length ? ` (${newTownNames.length})` : ""}
              </Button>
            </div>
          </div>
          <div className="overflow-y-auto divide-y divide-slate-100" style={{ maxHeight: "56vh" }}>
            {territory.groups.map((g) => {
              const inGroup = g.towns.filter((t) => checked.has(t.name)).length;
              return (
                <div key={g.key} data-testid={`territory-group-${g.key}`}>
                  <div className="sticky top-0 z-10 bg-slate-50 px-3 py-2 flex items-center justify-between gap-2 border-b border-slate-100">
                    <div className="min-w-0">
                      <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-600 truncate">{g.label}</div>
                      <div className="text-[10px] text-slate-400">{inGroup}/{g.towns.length} selected</div>
                    </div>
                    <div className="flex gap-1 shrink-0">
                      <button type="button" onClick={() => setGroup(g, true)} className="text-[10px] text-slate-500 hover:text-slate-900 underline" data-testid={`territory-group-all-${g.key}`}>all</button>
                      <span className="text-slate-300">·</span>
                      <button type="button" onClick={() => setGroup(g, false)} className="text-[10px] text-slate-500 hover:text-slate-900 underline" data-testid={`territory-group-none-${g.key}`}>none</button>
                    </div>
                  </div>
                  {g.towns.map((t) => (
                    <TownRow
                      key={t.name}
                      town={t}
                      on={checked.has(t.name)}
                      realtorCount={territory.realtor_counts?.[t.name] || 0}
                      scan={territory.last_scanned?.[t.name]}
                      onToggle={() => toggleTown(t.name)}
                      otherGroups={territory.editable ? territory.groups.filter((x) => x.key !== g.key) : []}
                      onMove={(toKey) => moveTown(t.name, toKey)}
                    />
                  ))}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Action + progress */}
      <div className="rounded-lg border border-slate-200 bg-white p-4" data-testid="territory-action-bar">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="text-xs text-slate-600 flex flex-col gap-2 max-w-xl">
            <span className="flex items-start gap-1.5">
              <Info className="h-3.5 w-3.5 text-slate-400 mt-0.5 shrink-0" />
              <span>
                Runs the Realtor.ca agent search once per selected town, keeps {territory.province || "province"} agents only,
                and files them into <span className="font-medium text-slate-900">{location?.name || "the territory"}</span> with
                duplicates merged. {checkedCount} search{checkedCount === 1 ? "" : "es"} will run.
              </span>
            </span>
            {checkedCount > 0 ? <CreditEstimate action="territory_import" params={{ towns: checkedCount }} onAllowed={setCreditsOk} /> : null}
          </div>
          <Button
            onClick={startImport}
            disabled={running || checkedCount === 0 || !location || !creditsOk}
            className="text-white min-h-[44px] shrink-0"
            style={{ background: "hsl(var(--brand))" }}
            data-testid="territory-start-import"
          >
            {running ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Play className="h-4 w-4 mr-2" />}
            {running ? "Importing…" : `Import ${checkedCount} town${checkedCount === 1 ? "" : "s"}`}
          </Button>
        </div>

        {job && isTerritoryJob ? (
          <div className="mt-4 border-t border-slate-100 pt-4" data-testid="territory-progress">
            {running ? (
              <>
                <div className="flex items-center justify-between text-xs text-slate-600 mb-1.5">
                  <span>
                    Town <span className="font-semibold text-slate-900 tabular-nums">{summary.towns_done || 0}</span> of{" "}
                    <span className="tabular-nums">{summary.towns_total || 0}</span>
                    {summary.current_town ? <> · <span className="text-slate-900">{summary.current_town}</span></> : null}
                  </span>
                  <span className="tabular-nums text-emerald-700 font-medium">+{summary.new_added || 0} new · {summary.merged_into_existing || 0} merged</span>
                </div>
                <div className="h-2 w-full rounded-full bg-slate-100 overflow-hidden">
                  <div
                    className="h-full bg-[#C8102E] transition-all"
                    style={{ width: `${summary.towns_total ? Math.round(((summary.towns_done || 0) / summary.towns_total) * 100) : 0}%` }}
                  />
                </div>
              </>
            ) : done ? (
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3" data-testid="territory-done">
                <div className="flex items-start gap-2 text-sm">
                  <CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0 mt-0.5" />
                  <div>
                    <div className="font-semibold text-slate-900">Import complete</div>
                    <div className="text-xs text-slate-600 mt-0.5">
                      {summary.towns_with_results || 0} of {summary.towns_total || 0} towns had agents ·{" "}
                      <span className="text-emerald-700 font-medium">{summary.new_added || 0} new</span>,{" "}
                      {summary.merged_into_existing || 0} merged{summary.apify_cost_usd ? ` · ~$${Number(summary.apify_cost_usd).toFixed(2)} Apify` : ""}
                    </div>
                  </div>
                </div>
                <Button variant="outline" size="sm" onClick={viewRealtors} className="shrink-0" data-testid="territory-view-realtors">
                  <Users className="h-3.5 w-3.5 mr-1.5" /> View realtors
                </Button>
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
        </>
      )}

      <ConfirmDialog
        open={confirmDelete}
        title={`Delete “${territory.name}”?`}
        description="This removes the territory definition (its drawn boundary and town list). Realtors already imported into the location stay intact."
        confirmLabel="Delete territory"
        destructive
        onConfirm={doDelete}
        onClose={() => setConfirmDelete(false)}
      />
    </div>
  );
}
